import { Button, Grid, Paper, Typography } from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import { ArrowForwardIos as ForwardIcon } from '@material-ui/icons';
import * as qs from 'query-string';
import React from 'react';
import { connect } from 'react-redux';

import { App, Booking, HELP_URL, TEE_TIME_MAX_PLAYERS, helpers } from '@aps-management/primapp-common';

import { Screen } from '_components/core';
import { ActivitySelector, Notice } from '_components/elements';
import apolloClient from '_utils/apolloClient';
import i18n from '_utils/i18n';
import { getAuthorization } from '_utils/security';

import PhoneAlert from './PhoneAlert';
import SetAccessories from './SetAccessories';
import SetCourse from './SetCourse';
import SetDate from './SetDate';
import SetPlayers from './SetPlayers';

/* */
const styles = (theme) => ({
  titleButton: {
    marginLeft: theme.spacing.unit * 2,
  },
  optionContainer: {
    alignItems: 'center',
    color: theme.palette.grey[500],
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    marginBottom: theme.spacing.unit * 2,
    padding: theme.spacing.unit * 2,
  },
  optionContent: {
    flex: 1,
    marginRight: theme.spacing.unit * 2,
    overflow: 'hidden',
  },
  optionValue: {
    color: theme.palette.text.secondary,
    lineHeight: 'inherit',
  },
});

/* */
const SearchOption = ({
  classes,
  label,
  onClick,
  value,
  isExpanded = false,
  warningMessage,
  warningComponent = null,
}) => (
  <Paper className={classes.optionContainer} onClick={onClick}>
    <div className={classes.optionContent}>
      <Typography gutterBottom color='inherit' component='span' variant='h6'>
        {label}
      </Typography>
      <Typography className={classes.optionValue} component='span' noWrap={true} variant='h5'>
        {value}
      </Typography>
      {warningMessage && (
        <Typography color='error' component='p' variant='caption'>
          {warningMessage}
        </Typography>
      )}
      {warningComponent}
    </div>
    {isExpanded ? (
      <ForwardIcon style={{ transform: 'rotate(90deg)' }} color='action' />
    ) : (
      <ForwardIcon color='action' />
    )}
  </Paper>
);

/* */
class BookingSearch extends React.Component {
  /* */
  constructor(props) {
    super(props);

    const { account, golf, location, search } = this.props;
    const { activity = null } = qs.parse(location.search);

    this.state = {
      error: null,
      loading: true,
      activeOption: 0,
      activity: Number(activity) || search.activity,
    };

    this.match = App.functions.match(account, golf) || { isMember: false, reference: 'UNKNOWN' };
  }

  /* */
  componentDidMount() {
    const {
      search,
      data: { initial, dates },
    } = this.props;

    if (!search.golf || !initial || !dates) {
      this.load();
    } else {
      // check if valid
      if (!dates.includes(search.date)) {
        this.props.setDate(initial.date);
      }
      if (!Booking.functions.checkCourseAvailability(search.course, search.date)) {
        this.props.setCourse(null);
      }
      this.setState({ loading: false });
    }
  }

  /* */
  load = () => {
    const { match } = this;
    const { activity } = this.state;
    const { account, golf, data } = this.props;
    const initialData = data?.booking;
    this.props.reset();
    this.setState({ loading: true, activeOption: 0 });

    Booking.api
      .selectGolf(apolloClient, {
        activity,
        id: golf.id,
        player: match.reference,
      })
      .then((result) => {
        // Set golf et reset search result
        this.props.setGolf(result.golf);
        this.props.setActivity(activity);

        if (result.courses && result.courses.length) {
          const { courses, initial, openDates, openCourses, availableDates } = Booking.functions.processCourses(
            result.courses,
          );

          // Save initial
          data.initial = initial;
          data.openDates = openDates;
          data.openCourses = openCourses;

          // Choose initial
          this.props.setCourse(initialData?.course || initial.course);
          this.props.setDate(initialData?.date || initial.date);

          const { email, phone, lastname, firstname } = account;

          this.props.addPlayer({
            email,
            phone,
            type: 'owner',
            isMember: match.isMember,
            reference: match.reference,
            lastname: lastname.toUpperCase(),
            firstname: helpers.string.ucfirst(firstname),
          });
          const initialPlayers = initialData?.players.filter((p) => p.type !== 'owner');
          if (initialPlayers) {
            initialPlayers.map((p) =>
              this.props.addPlayer({
                email: null,
                phone: null,
                lastname: null,
                firstname: null,
                isMember: false,
                type: 'anonymous',
                reference: p,
              }),
            );
          }

          this.props.setCoursesList(courses);
          this.props.setDatesList(availableDates);
          this.props.setAccessoriesList(result.accessories);
        } else {
          // Reset sur reload (= changement d'activité)
          this.props.setCoursesList([]);
          this.props.setDatesList([]);
          this.props.setAccessoriesList([]);
        }
      })
      .catch((err) => {
        this.setState({ error: err.message });
      })
      .finally(() => this.setState({ loading: false }));
  };

  /* */
  changeActivity = (activity) => this.setState({ activity }, () => this.load());

  /* */
  renderDateOption() {
    const { activeOption } = this.state;
    const {
      classes,
      search: { date },
      width,
    } = this.props;

    const value = helpers.string.ucfirst(i18n.l('date.formats.long', new Date(date)));

    return (
      <SearchOption
        label='Date'
        value={value}
        classes={classes}
        onClick={() => this.setState({ activeOption: 0 })}
        isExpanded={!isWidthUp('md', width) && activeOption === 0}
      />
    );
  }

  /* */
  renderCourseOption() {
    const { activeOption, activity } = this.state;
    const {
      golf,
      width,
      classes,
      search: { course },
    } = this.props;

    const { name, type } = course || { name: null, type: null };

    let value;
    if (activity && activity !== 1) {
      value = name || '-';
    } else {
      const nbOfHoles = i18n.t(`terms.course_type_holes_${type}`, { defaultValue: '' });
      value = name ? `${!name.includes(nbOfHoles) ? `(${nbOfHoles} T) ` : ''}${name}` : '-';
    }

    const label = i18n.getFromOpts('booking.titles.course', golf.options, { activity });

    let warningComponent = null;
    if (typeof course?.handicap === 'number') {
      warningComponent = (
        <Box mt={1.5}>
          <Typography variant='caption'>
            <b>{"Condition(s) d'accès"}</b>
          </Typography>
          <Typography variant='caption'>
            {' - Avoir un index inférieur ou égal à '}
            <u>{`${course.handicap.toFixed(1)}`}</u>
          </Typography>
        </Box>
      );
    }

    return (
      <SearchOption
        label={label}
        value={value}
        classes={classes}
        warningComponent={warningComponent}
        onClick={() => this.setState({ activeOption: 1 })}
        isExpanded={!isWidthUp('md', width) && activeOption === 1}
      />
    );
  }

  /* */
  renderPlayersOption() {
    const { activeOption } = this.state;
    const {
      classes,
      search: { course, players },
      width,
    } = this.props;

    const numberOfPlayers = players.length;
    const defaultCourse = { anonymous: true, maxPlayers: TEE_TIME_MAX_PLAYERS };
    const { anonymous = true, maxPlayers: maxNumberOfPlayers } = course || defaultCourse;

    const value = i18n.t('terms.players', { count: numberOfPlayers });

    let warningMessage = null;
    if (numberOfPlayers > maxNumberOfPlayers) {
      warningMessage = `Veuillez ne pas dépasser la limite autorisée (${maxNumberOfPlayers} maximum).`;
    }
    if (!anonymous && players.some((p) => p.type === 'anonymous')) {
      warningMessage = 'Veuillez identifier les joueurs.';
    }

    return (
      <SearchOption
        value={value}
        classes={classes}
        label='Joueur(s)'
        warningMessage={warningMessage}
        onClick={() => this.setState({ activeOption: 2 })}
        isExpanded={!isWidthUp('md', width) && activeOption === 2}
      />
    );
  }

  /* */
  renderAccessoriesOption() {
    const { activeOption, activity } = this.state;
    const { data, golf, width, search, classes } = this.props;

    const { allowAccessories = true } = golf.options.activities[activity] || {};

    if (!allowAccessories || data.accessories.length === 0) return null;

    const accsQty = search.accessoriesTotalQty;
    const numberOfPlayers = search.players.length;

    const value = helpers.string.ucfirst(i18n.t('terms.rentals', { count: accsQty }));
    const warning = `Veuillez ne pas dépasser la limite autorisée (${numberOfPlayers} maximum).`;

    return (
      <SearchOption
        value={value}
        classes={classes}
        label='Accessoire(s)'
        onClick={() => this.setState({ activeOption: 3 })}
        isExpanded={!isWidthUp('md', width) && activeOption === 3}
        warningMessage={accsQty > numberOfPlayers ? warning : null}
      />
    );
  }

  /* */
  isAuthorized(path) {
    const { golf, account } = this.props;
    const { isAuthorized } = getAuthorization(path, { golf, account });

    return isAuthorized;
  }

  /* */
  renderUnavailabilityMsg() {
    return (
      <Typography paragraph variant='h5' align='center' component='h2'>
        <br />
        {'Aucune disponibilité pour le moment.'}
      </Typography>
    );
  }

  /* */
  renderActivities() {
    const { golf } = this.props;
    const { activities } = golf.options;
    const { activity: currentActivity } = this.state;

    if (this.isAuthorized('/activity')) return null;

    return <ActivitySelector data={activities} value={currentActivity} onChange={this.changeActivity} />;
  }

  /* */
  renderAllOptions() {
    const { activeOption, activity } = this.state;
    const {
      data: { dates, courses },
      width,
    } = this.props;

    // Aucune date disponible pour les parcours
    if (courses.length === 0 || dates.length === 0) {
      return this.renderUnavailabilityMsg();
    }

    if (isWidthUp('md', width)) {
      return (
        <Grid container spacing={24}>
          <Grid item md={6}>
            {this.renderDateOption()}
            {this.renderCourseOption()}
            {this.renderPlayersOption()}
            {this.renderAccessoriesOption()}
            <PhoneAlert />
            <br />
            {this.renderButton()}
          </Grid>
          <Grid item md={6}>
            {activeOption === 0 && <SetDate />}
            {activeOption === 1 && <SetCourse activity={activity} />}
            {activeOption === 2 && <SetPlayers />}
            {activeOption === 3 && <SetAccessories />}
          </Grid>
        </Grid>
      );
    }

    return (
      <React.Fragment>
        {this.renderDateOption()}
        {activeOption === 0 && <SetDate />}
        {this.renderCourseOption()}
        {activeOption === 1 && <SetCourse activity={activity} />}
        {this.renderPlayersOption()}
        {activeOption === 2 && <SetPlayers />}
        {this.renderAccessoriesOption()}
        {activeOption === 3 && <SetAccessories />}
        <PhoneAlert />
        <br />
        {this.renderButton()}
      </React.Fragment>
    );
  }

  /* */
  renderButton() {
    const { history, search, account } = this.props;

    if (!search.course) return null;

    const accsQty = search.accessoriesTotalQty;
    const numberOfPlayers = search.players.length;
    const { maxPlayers: maxNumberOfPlayers, anonymous = true } = search.course;

    const checkPlayers = anonymous || search.players.every((p) => p.type !== 'anonymous');

    if (
      checkPlayers &&
      search.golf != null &&
      search.date != null &&
      numberOfPlayers > 0 &&
      accsQty <= numberOfPlayers &&
      numberOfPlayers <= maxNumberOfPlayers
    ) {
      return (
        <Button
          fullWidth
          size='large'
          color='secondary'
          variant='contained'
          disabled={!account.phone}
          onClick={() => history.push('/booking/result')}
        >
          {'Voir les disponibilités'}
        </Button>
      );
    }

    return null;
  }

  /* */
  render() {
    const { error, loading } = this.state;

    return (
      <Screen
        error={error}
        title='Réserver'
        loading={loading}
        helpURL={`${HELP_URL}/r%C3%A9server`}
        onRetry={this.load}
      >
        <Notice context='BOOKING' />
        {this.renderActivities()}
        {!loading && this.renderAllOptions()}
      </Screen>
    );
  }
}

/* */
const mapStateToProps = ({ app, bookingData, bookingSearch }) => ({
  golf: app.golf,
  data: bookingData,
  account: app.account,
  search: bookingSearch,
});

const StyledComponent = withStyles(styles)(BookingSearch);

export default connect(mapStateToProps, Booking.actions)(withWidth()(StyledComponent));
